/**
 * TanStack Query — клиентская часть API (docs/API.md §2–§3).
 *
 * `qk` — единственный источник ключей. Любая инвалидация ссылается на него,
 * иначе ключи расходятся и кэш живёт своей жизнью.
 *
 * Деньги никогда не обновляются оптимистично: единственный источник истины —
 * сервер (D-07). Оптимистично меняются только булевы переключатели.
 */

import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type UseInfiniteQueryResult,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { useAuthStore } from '@/shared/hooks/useAuth';
import { markUnlocked } from '@/shared/lib/pinLock';

import { api, tokenStore } from './client';
import type {
  AchievementsResponse,
  ActiveRound,
  AdminClient,
  AdminFaqEntry,
  AuditEntry,
  CoreState,
  StaffMember,
  DepositClaim,
  DepositClaimInput,
  DepositInfo,
  FaqItem,
  GlobalStats,
  Locale,
  AdminRound,
  Me,
  MyRing,
  MyStats,
  Paged,
  PaymentHistoryPage,
  ParticipationSettingsPatch,
  PayoutNetwork,
  ProfilePatch,
  PublicConfig,
  PublicStats,
  ReferralStats,
  RequestLevelPayload,
  Withdrawal,
} from './types';

/* -------------------------------------------------------------------------- */
/* Ключи                                                                      */
/* -------------------------------------------------------------------------- */

export const qk = {
  publicConfig: ['public', 'config'] as const,
  publicStats: ['public', 'stats'] as const,
  publicMaxLevel: ['public', 'max-ring-level'] as const,
  faq: (locale: string) => ['public', 'faq', locale] as const,

  me: ['client', 'me'] as const,
  deposit: ['client', 'deposit'] as const,
  depositClaims: ['client', 'deposits'] as const,
  rounds: ['client', 'rounds'] as const,
  activeRound: ['client', 'rounds', 'active'] as const,
  round: (id: number) => ['client', 'rounds', id] as const,
  watchRound: (id: number) => ['client', 'rounds', 'watch', id] as const,
  history: ['client', 'rounds', 'history'] as const,
  myStats: ['client', 'stats', 'me'] as const,
  globalStats: ['client', 'stats', 'global'] as const,
  withdrawals: ['client', 'withdrawals'] as const,
  achievements: ['client', 'achievements'] as const,
  referrals: ['client', 'referrals'] as const,
  referralsLink: ['client', 'referrals', 'link'] as const,
  referralsList: ['client', 'referrals', 'list'] as const,
  referralsTree: (depth: number) => ['client', 'referrals', 'tree', depth] as const,
  paymentHistory: ['client', 'payment-history'] as const,

  admin: {
    root: ['admin'] as const,
    dashboard: ['admin', 'dashboard'] as const,
    clients: (search: string) => ['admin', 'clients', { search }] as const,
    /**
     * Поиск клиентов держим в отдельном пространстве: список клиентов —
     * бесконечный запрос ({pages, pageParams}), поиск — обычный (Paged<T>).
     * На одном ключе они читали кэш друг друга в чужой форме, и экран,
     * открытый вторым, получал `items === undefined`.
     */
    clientsSearch: (query: string) => ['admin', 'clients', 'search', query] as const,
    client: (id: number) => ['admin', 'clients', id] as const,
    core: ['admin', 'core'] as const,
    // Фильтры входят в ключ: иначе список под разными фильтрами делил бы кэш и
    // показывал чужие строки до перезапроса.
    withdrawals: (status: string, network?: string, q?: string, minAmount?: string) =>
      ['admin', 'withdrawals', { status, network, q, minAmount }] as const,
    // Фильтры в ключе — по той же причине, что и у выводов.
    deposits: (status?: string, network?: string, q?: string) =>
      ['admin', 'deposits', { status, network, q }] as const,
    activeRound: ['admin', 'rounds', 'active'] as const,
    ringSet: ['admin', 'rounds', 'ring-set'] as const,
    profitShares: ['admin', 'rounds', 'profit-shares'] as const,
    rounds: ['admin', 'rounds', 'history'] as const,
    previewClose: (roundId: number, value: string, inputMode: string) =>
      ['admin', 'rounds', roundId, 'preview-close', { value, inputMode }] as const,
    broadcasts: ['admin', 'broadcasts'] as const,
    faq: ['admin', 'faq'] as const,
    audit: (filters: { entity?: string; action?: string }) =>
      ['admin', 'audit', filters] as const,
    staff: ['admin', 'staff'] as const,
    settings: ['admin', 'settings'] as const,
    referralsOverview: ['admin', 'referrals', 'overview'] as const,
    referralStats: (userId: number) => ['admin', 'referrals', 'stats', userId] as const,
    referralTree: (userId: number, depth: number) => ['admin', 'referrals', 'tree', userId, depth] as const,
    searchReferrers: (query: string) => ['admin', 'referrals', 'search', query] as const,
    levelRequests: ['admin', 'level-requests'] as const,
  },
} as const;

/** Страница истории: сервер сам решает размер, клиент только просит лимит. */
const PAGE_SIZE = 30;

/* -------------------------------------------------------------------------- */
/* Public                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Конфиг живёт долго: расписание рынка и адреса меняются редко, а server_time
 * нужен только для вычисления skew один раз за сессию (D-10).
 */
export function usePublicConfig(): UseQueryResult<PublicConfig, unknown> {
  return useQuery({
    queryKey: qk.publicConfig,
    queryFn: ({ signal }) => api.get<PublicConfig>('/public/config', { signal, anonymous: true }),
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
  });
}

export function usePublicStats(): UseQueryResult<PublicStats, unknown> {
  return useQuery({
    queryKey: qk.publicStats,
    queryFn: ({ signal }) => api.get<PublicStats>('/public/stats', { signal, anonymous: true }),
    staleTime: 45_000,
  });
}

export function useFaq(locale: string): UseQueryResult<FaqItem[], unknown> {
  return useQuery({
    queryKey: qk.faq(locale),
    queryFn: ({ signal }) =>
      api.get<FaqItem[]>('/public/faq', { signal, anonymous: true, query: { locale } }),
    staleTime: 10 * 60_000,
  });
}

export function useMaxRingLevel(): UseQueryResult<{ max_level: number }, unknown> {
  return useQuery({
    queryKey: qk.publicMaxLevel,
    queryFn: ({ signal }) =>
      api.get<{ max_level: number }>('/public/max-ring-level', { signal, anonymous: true }),
    staleTime: 2 * 60_000,
  });
}

/* -------------------------------------------------------------------------- */
/* Client: профиль                                                            */
/* -------------------------------------------------------------------------- */

export function useMe(enabled = true): UseQueryResult<Me, unknown> {
  const qc = useQueryClient();
  return useQuery({
    queryKey: qk.me,
    queryFn: async ({ signal }) => {
      const me = await api.get<Me>('/client/me', { signal });
      const previous = qc.getQueryData<Me>(qk.me);
      if (previous && (previous.current_level ?? 1) !== (me.current_level ?? 1)) {
        void qc.invalidateQueries({ queryKey: qk.rounds });
      }
      return me;
    },
    enabled,
    staleTime: 60_000,
    refetchInterval: (query) => query.state.data?.pending_level ? 15_000 : false,
    refetchIntervalInBackground: false,
  });
}

export function useDeposit(enabled = true): UseQueryResult<DepositInfo, unknown> {
  return useQuery({
    queryKey: qk.deposit,
    queryFn: ({ signal }) => api.get<DepositInfo>('/client/me/deposit', { signal }),
    enabled,
    staleTime: 5 * 60_000,
  });
}

/**
 * Свои заявки на пополнение.
 *
 * Пока хоть одна заявка не закрыта, список опрашивается раз в 20 с: клиент
 * видит, как растут подтверждения, и не жмёт «обновить». Когда всё закрыто,
 * поллинг выключается — незачем будить сеть ради статики.
 */
export function useDepositClaims(enabled = true): UseQueryResult<DepositClaim[], unknown> {
  return useQuery({
    queryKey: qk.depositClaims,
    queryFn: ({ signal }) => api.get<DepositClaim[]>('/client/deposits', { signal }),
    enabled,
    staleTime: 10_000,
    refetchInterval: (query) => {
      const items = query.state.data;
      if (!items) return false;
      const pending = items.some((c) => c.status === 'pending' || c.status === 'confirming');
      return pending ? 20_000 : false;
    },
    refetchIntervalInBackground: false,
  });
}

/**
 * Заявить о переводе по хэшу транзакции.
 *
 * Инвалидируем и профиль: если транзакция уже набрала подтверждения, воркер
 * зачислит её почти сразу, и capital в шапке должен догнать реальность.
 */
export function useClaimDeposit(): UseMutationResult<
  DepositClaim,
  unknown,
  DepositClaimInput,
  unknown
> {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (payload: DepositClaimInput) =>
      api.post<DepositClaim>('/client/deposits/claim', payload),
    onSuccess: () => invalidate([[...qk.depositClaims], [...qk.me], [...qk.myStats]]),
  });
}

/* -------------------------------------------------------------------------- */
/* Client: кольца                                                             */
/* -------------------------------------------------------------------------- */

/**
 * Активные кольца опрашиваются раз в 15 с, но только на видимой вкладке:
 * в свёрнутом TMA поллинг бессмысленно жжёт батарею, а «живая сумма»
 * пересчитывается локально из active_seconds (D-10).
 */
export function useActiveRounds(enabled = true): UseQueryResult<ActiveRound[], unknown> {
  return useQuery({
    queryKey: qk.activeRound,
    queryFn: ({ signal }) => api.get<ActiveRound[]>('/client/rounds/active', { signal }),
    enabled,
    staleTime: 10_000,
    refetchInterval: 15_000,
    refetchIntervalInBackground: false,
  });
}

/** @deprecated Use useActiveRounds for multi-tier support */
export function useActiveRound(enabled = true): UseQueryResult<ActiveRound | null, unknown> {
  return useQuery({
    queryKey: qk.activeRound,
    queryFn: ({ signal }) => api.get<ActiveRound[]>('/client/rounds/active', { signal }),
    select: (rounds) => rounds[0] ?? null,
    enabled,
    staleTime: 10_000,
    refetchInterval: 15_000,
    refetchIntervalInBackground: false,
  });
}

/**
 * Полноэкранный просмотр своего кольца.
 *
 * Отдельный ключ, а не `qk.round`: ответ `/watch` — это живой срез
 * (анимация, участники), а `qk.round` — статичная карточка участия.
 */
export function useWatchRound(id: number | null): UseQueryResult<ActiveRound, unknown> {
  return useQuery({
    queryKey: qk.watchRound(id ?? 0),
    queryFn: ({ signal }) =>
      api.get<ActiveRound>(`/client/rounds/${String(id)}/watch`, { signal }),
    enabled: id !== null,
    staleTime: 5_000,
    refetchInterval: 15_000,
    refetchIntervalInBackground: false,
  });
}

export function useRound(id: number | null): UseQueryResult<MyRing, unknown> {
  return useQuery({
    queryKey: qk.round(id ?? 0),
    queryFn: ({ signal }) => api.get<MyRing>(`/client/rounds/${String(id)}`, { signal }),
    enabled: id !== null,
    staleTime: 5 * 60_000,
  });
}

export function useHistory(
  limit = PAGE_SIZE,
): UseInfiniteQueryResult<{ pages: Paged<MyRing>[]; pageParams: (string | null)[] }, unknown> {
  return useInfiniteQuery({
    queryKey: qk.history,
    queryFn: ({ pageParam, signal }) =>
      api.get<Paged<MyRing>>('/client/rounds/history', {
        signal,
        query: { limit, cursor: pageParam },
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor,
    staleTime: 60_000,
  });
}

export function useAllRounds(
  limit = PAGE_SIZE,
): UseInfiniteQueryResult<{ pages: Paged<MyRing>[]; pageParams: (string | null)[] }, unknown> {
  return useInfiniteQuery({
    queryKey: [...qk.history, 'all'],
    queryFn: ({ pageParam, signal }) =>
      api.get<Paged<MyRing>>('/client/rounds/all', {
        signal,
        query: { limit, cursor: pageParam },
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor,
    staleTime: 60_000,
  });
}

export function useMyStats(enabled = true): UseQueryResult<MyStats, unknown> {
  return useQuery({
    queryKey: qk.myStats,
    queryFn: ({ signal }) => api.get<MyStats>('/client/stats/me', { signal }),
    enabled,
    staleTime: 60_000,
  });
}

export function useGlobalStats(enabled = true): UseQueryResult<GlobalStats, unknown> {
  return useQuery({
    queryKey: qk.globalStats,
    queryFn: ({ signal }) => api.get<GlobalStats>('/client/stats/global', { signal }),
    enabled,
    staleTime: 45_000,
  });
}

export function useWithdrawals(enabled = true): UseQueryResult<Withdrawal[], unknown> {
  return useQuery({
    queryKey: qk.withdrawals,
    queryFn: ({ signal }) => api.get<Withdrawal[]>('/client/withdrawals', { signal }),
    enabled,
    staleTime: 30_000,
  });
}

export function useAchievements(enabled = true): UseQueryResult<AchievementsResponse, unknown> {
  return useQuery({
    queryKey: qk.achievements,
    queryFn: ({ signal }) => api.get<AchievementsResponse>('/client/achievements', { signal }),
    enabled,
    staleTime: 30_000,
    refetchOnMount: 'always',
  });
}

const PAYMENT_HISTORY_PAGE_SIZE = 20;

export function usePaymentHistory(
  page = 0,
  enabled = true,
): UseQueryResult<PaymentHistoryPage & { offset: number }, unknown> {
  const offset = page * PAYMENT_HISTORY_PAGE_SIZE;
  return useQuery({
    queryKey: [...qk.paymentHistory, { limit: PAYMENT_HISTORY_PAGE_SIZE, offset }],
    queryFn: async ({ signal }) => {
      const response = await api.get<PaymentHistoryPage>('/client/me/payment-history', {
        signal,
        query: { limit: PAYMENT_HISTORY_PAGE_SIZE, offset },
      });
      // Keep the range attached to its rows while the next page is loading.
      return { ...response, offset };
    },
    placeholderData: (previous) => previous,
    enabled,
    staleTime: 30_000,
    gcTime: 60_000,
    refetchOnWindowFocus: false,
  });
}

export function useReferralLink(enabled = true): UseQueryResult<{ code: string; link: string }, unknown> {
  return useQuery({
    queryKey: qk.referralsLink,
    queryFn: ({ signal }) => api.get<{ code: string; link: string }>('/client/referrals/link', { signal }),
    enabled,
    staleTime: 5 * 60_000,
  });
}

export function useReferralStats(
  enabled = true,
  limit = 20,
  level: number | null = null,
): UseInfiniteQueryResult<{ pages: ReferralStats[]; pageParams: (string | null)[] }, unknown> {
  return useInfiniteQuery({
    queryKey: [...qk.referrals, limit, level],
    queryFn: ({ pageParam, signal }) =>
      api.get<ReferralStats>('/client/referrals', {
        signal,
        query: { limit, cursor: pageParam, level },
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.next_cursor,
    enabled,
    staleTime: 30_000,
  });
}

/* -------------------------------------------------------------------------- */
/* Мутации                                                                    */
/* -------------------------------------------------------------------------- */

/** Всё, что зависит от денег и участия. Инвалидируется вместе. */
function moneyKeys(): readonly unknown[][] {
  return [
    [...qk.me],
    [...qk.activeRound],
    [...qk.history],
    [...qk.myStats],
    [...qk.globalStats],
    [...qk.withdrawals],
    [...qk.achievements],
  ];
}

function useInvalidate(): (keys: readonly unknown[][]) => Promise<void> {
  const qc = useQueryClient();
  return async (keys) => {
    await Promise.all(keys.map((queryKey) => qc.invalidateQueries({ queryKey })));
  };
}

export function useJoinRound(): UseMutationResult<ActiveRound | null, unknown, number, unknown> {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (roundId: number) =>
      api.post<ActiveRound | null>(`/client/rounds/${String(roundId)}/join`),
    onSuccess: () => invalidate(moneyKeys()),
  });
}

export function useLeaveRound(): UseMutationResult<void, unknown, number, unknown> {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (roundId: number) =>
      api.del<void>(`/client/rounds/${String(roundId)}/participation`),
    onSuccess: () => invalidate(moneyKeys()),
  });
}

export function useUpdateParticipation(): UseMutationResult<Me, unknown, ParticipationSettingsPatch, unknown> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: ParticipationSettingsPatch) =>
      api.patch<Me>('/client/me/participation', patch),
    onSuccess: async (me) => {
      await qc.cancelQueries({ queryKey: qk.me });
      qc.setQueryData(qk.me, me);
      return qc.invalidateQueries({ queryKey: qk.rounds });
    },
  });
}

export function useUpdateProfile(): UseMutationResult<Me, unknown, ProfilePatch, unknown> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: ProfilePatch) => api.patch<Me>('/client/me/profile', patch),
    onSuccess: async (me) => {
      await qc.cancelQueries({ queryKey: qk.me });
      qc.setQueryData(qk.me, me);
    },
  });
}

export function useUpdateLocale(): UseMutationResult<Me, unknown, Locale, unknown> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (locale: Locale) => api.patch<Me>('/client/me/locale', { locale }),
    onSuccess: (me) => {
      qc.setQueryData(qk.me, me);
    },
  });
}

export interface NotificationsPatch {
  notify_digest?: boolean;
  notify_events?: boolean;
}

export function useUpdateNotifications(): UseMutationResult<
  Me,
  unknown,
  NotificationsPatch,
  { previous: Me | undefined }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: NotificationsPatch) => api.patch<Me>('/client/me/notifications', patch),
    onMutate: async (patch) => {
      await qc.cancelQueries({ queryKey: qk.me });
      const previous = qc.getQueryData<Me>(qk.me);
      if (previous) qc.setQueryData<Me>(qk.me, { ...previous, ...patch });
      return { previous };
    },
    onError: (_error, _vars, context) => {
      if (context?.previous) qc.setQueryData<Me>(qk.me, context.previous);
    },
    onSuccess: (me) => {
      qc.setQueryData(qk.me, me);
    },
  });
}

/* -------------------------------------------------------------------------- */
/* Пин-код                                                                    */
/* -------------------------------------------------------------------------- */

export interface PinSetInput {
  pin: string;
  /** Обязателен, если пин уже задан: смена замка требует текущего. */
  current_pin?: string;
}

/** Задать или сменить пин. Ответ — свежий профиль с обновлённым `pin_set`. */
export function useSetPin(): UseMutationResult<Me, unknown, PinSetInput, unknown> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: PinSetInput) => api.patch<Me>('/client/me/pin', input),
    onSuccess: (me) => {
      // Сервер уже подтвердил PIN: сохраняем открытой текущую сессию.
      markUnlocked();
      useAuthStore.getState().setMe(me);
      qc.setQueryData(qk.me, me);
    },
  });
}

/** Снять пин: нужен текущий. */
export function useRemovePin(): UseMutationResult<Me, unknown, string, unknown> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (pin: string) => api.post<Me>('/client/me/pin/remove', { pin }),
    onSuccess: (me) => {
      useAuthStore.getState().setMe(me);
      qc.setQueryData(qk.me, me);
    },
  });
}

/**
 * Проверка пина на экране блокировки. Ошибка = пин неверный; 429 — попытки
 * исчерпаны (сервер ограничивает 10 проверок за 5 минут).
 */
export function useVerifyPin(): UseMutationResult<void, unknown, string, unknown> {
  return useMutation({
    mutationFn: (pin: string) => api.post<void>('/client/me/pin/verify', { pin }),
  });
}

export interface WithdrawalRequest {
  amount: string;
  network: PayoutNetwork;
}

export function useRequestWithdrawal(): UseMutationResult<
  Withdrawal,
  unknown,
  WithdrawalRequest,
  unknown
> {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (payload: WithdrawalRequest) => api.post<Withdrawal>('/client/withdrawals', payload),
    // Резерв меняет accrued_profit/reserved_profit — только инвалидация (D-07).
    onSuccess: () => invalidate([[...qk.me], [...qk.withdrawals], [...qk.myStats], [...qk.achievements]]),
  });
}

export function useDeleteAccount(): UseMutationResult<void, unknown, void, unknown> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.del<void>('/client/me', { confirm: 'DELETE' }),
    onSuccess: () => {
      tokenStore.clear();
      qc.clear();
    },
  });
}

/* -------------------------------------------------------------------------- */
/* Admin                                                                      */
/* -------------------------------------------------------------------------- */

export function useCoreState(enabled = true): UseQueryResult<CoreState, unknown> {
  return useQuery({
    queryKey: qk.admin.core,
    queryFn: ({ signal }) => api.get<CoreState>('/admin/core', { signal }),
    enabled,
    staleTime: 30_000,
  });
}

export function useManualTopup(): UseMutationResult<CoreState, unknown, { amount: string; note?: string }, unknown> {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (payload) => api.post<CoreState>('/admin/core/topups', payload),
    onSuccess: () => invalidate([[...qk.admin.core]]),
  });
}

export function useAdminRounds(filters?: {
  status?: string;
}): UseQueryResult<Paged<AdminRound>, unknown> {
  return useQuery({
    queryKey: [...qk.admin.rounds, filters],
    queryFn: ({ signal }) =>
      api.get<Paged<AdminRound>>('/admin/rounds', { signal, query: filters }),
    staleTime: 30_000,
  });
}

/**
 * Рассылка объявления. Контракт сервера — `{text, only_linked}` (docs/API.md
 * §4): `text` строкой = один текст для всех локалей, `only_linked` = только тем,
 * у кого привязан Telegram (иначе доставлять некуда).
 */
export function useBroadcast(): UseMutationResult<unknown, unknown, { message: string }, unknown> {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ message }) =>
      api.post<unknown>('/admin/broadcast', { text: message, only_linked: true }),
    onSuccess: () => invalidate([[...qk.admin.broadcasts]]),
  });
}

export function useAdminFaq(enabled = true): UseQueryResult<AdminFaqEntry[], unknown> {
  return useQuery({
    queryKey: qk.admin.faq,
    queryFn: ({ signal }) => api.get<AdminFaqEntry[]>('/admin/faq', { signal }),
    enabled,
    staleTime: 5 * 60_000,
  });
}

/** Тело создания/правки записи FAQ: i18n-объекты вопроса и ответа. */
export type FaqEntryInput = Omit<AdminFaqEntry, 'id'>;

export function useCreateFaqEntry(): UseMutationResult<void, unknown, FaqEntryInput, unknown> {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (payload) => api.post<void>('/admin/faq', payload),
    onSuccess: () => invalidate([[...qk.admin.faq]]),
  });
}

export function useUpdateFaqEntry(): UseMutationResult<
  void,
  unknown,
  { id: number; data: Partial<FaqEntryInput> },
  unknown
> {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, data }) => api.patch<void>(`/admin/faq/${id}`, data),
    onSuccess: () => invalidate([[...qk.admin.faq]]),
  });
}

export function useDeleteFaqEntry(): UseMutationResult<void, unknown, number, unknown> {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (id) => api.del<void>(`/admin/faq/${id}`),
    onSuccess: () => invalidate([[...qk.admin.faq]]),
  });
}

export interface AuditFilters {
  action?: string;
  entity?: string;
  actor_id?: string;
  date_from?: string;
  date_to?: string;
}

export function useAuditLog(
  filters: AuditFilters = {},
): UseQueryResult<Paged<AuditEntry>, unknown> {
  return useQuery({
    queryKey: qk.admin.audit(filters),
    queryFn: ({ signal }) =>
      api.get<Paged<AuditEntry>>('/admin/audit', { signal, query: { ...filters } }),
    staleTime: 30_000,
  });
}

/** Отдаёт плоский массив, а не страницу — см. docs/API.md §5. */
export function useStaffList(enabled = true): UseQueryResult<StaffMember[], unknown> {
  return useQuery({
    queryKey: qk.admin.staff,
    queryFn: ({ signal }) => api.get<StaffMember[]>('/admin/staff', { signal }),
    enabled,
    staleTime: 60_000,
  });
}

export function useGrantAdmin(): UseMutationResult<void, unknown, number, unknown> {
  const invalidate = useInvalidate();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId) => api.post<void>('/admin/staff/grant', { user_id: userId }),
    onSuccess: async () => {
      await invalidate([[...qk.admin.staff]]);
      // Инвалидируем qk.me и рефетчим его, чтобы обновить роль текущего пользователя
      await queryClient.invalidateQueries({ queryKey: qk.me });
      // Рефетчим немедленно, чтобы обновить useAuth() store
      const freshMe = await queryClient.fetchQuery({
        queryKey: qk.me,
        queryFn: ({ signal }) => api.get<Me>('/client/me', { signal }),
      });
      // Обновляем zustand store для синхронизации с useAuth()
      const { useAuthStore } = await import('@/shared/hooks/useAuth');
      useAuthStore.getState().setMe(freshMe);
    },
  });
}

export function useRevokeAdmin(): UseMutationResult<void, unknown, number, unknown> {
  const invalidate = useInvalidate();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId) => api.post<void>('/admin/staff/revoke', { user_id: userId }),
    onSuccess: async () => {
      await invalidate([[...qk.admin.staff]]);
      // Инвалидируем qk.me и рефетчим его, чтобы обновить роль текущего пользователя
      await queryClient.invalidateQueries({ queryKey: qk.me });
      // Рефетчим немедленно, чтобы обновить useAuth() store
      const freshMe = await queryClient.fetchQuery({
        queryKey: qk.me,
        queryFn: ({ signal }) => api.get<Me>('/client/me', { signal }),
      });
      // Обновляем zustand store для синхронизации с useAuth()
      const { useAuthStore } = await import('@/shared/hooks/useAuth');
      useAuthStore.getState().setMe(freshMe);
    },
  });
}

export function useSearchClients(
  query: string,
  options?: { enabled?: boolean },
): UseQueryResult<Paged<AdminClient>, unknown> {
  return useQuery({
    queryKey: qk.admin.clientsSearch(query),
    // Параметр поиска на сервере называется `q` (docs/API.md §4): с `search`
    // фильтр молча игнорировался и выдача была полным списком клиентов.
    queryFn: ({ signal }) =>
      api.get<Paged<AdminClient>>('/admin/clients', { signal, query: { q: query } }),
    enabled: options?.enabled ?? true,
    staleTime: 30_000,
  });
}

// Настройки живут в adminQueries (`useSettings` / `useUpdateSetting`): там верный
// путь /admin/staff/settings и контракт правки `{key, value}`. Дубликаты, которые
// стучались в несуществующий /admin/settings, удалены — экран навсегда висел
// в скелете, потому что запрос всегда отвечал 404.

/* -------------------------------------------------------------------------- */
/* Level Requests                                                              */
/* -------------------------------------------------------------------------- */

export function useRequestLevel(): UseMutationResult<
  void,
  unknown,
  RequestLevelPayload,
  unknown
> {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (payload) => api.post<void>('/client/me/request-level', payload),
    onSuccess: () => invalidate([[...qk.me]]),
  });
}
